/**
 * SignupStatusBanner
 *
 * Shows contextual banner based on guide signup/approval status.
 * Banner logic lives here — NOT in OnboardingProgressWidget.
 *
 * SCRUM-1860: 4 banner states driven by onboarding-progress payload fields.
 */

import React from 'react';
import { AlertCircle, Clock, CreditCard } from 'lucide-react';

const BANNER_STYLES = {
  warning: {
    background: '#fffbeb',
    borderColor: '#f59e0b',
    iconColor: '#d97706',
    textColor: '#92400e',
  },
  info: {
    background: '#eff6ff',
    borderColor: '#3b82f6',
    iconColor: '#2563eb',
    textColor: '#1e40af',
  },
  success: {
    background: '#f0fdf4',
    borderColor: '#22c55e',
    iconColor: '#16a34a',
    textColor: '#14532d',
  },
};

function Banner({ variant, icon: Icon, title, message, action }) {
  const s = BANNER_STYLES[variant] || BANNER_STYLES.info;
  return (
    <div style={{
      background: s.background,
      border: `1px solid ${s.borderColor}`,
      borderRadius: '8px',
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    }}>
      <Icon size={20} style={{ color: s.iconColor, flexShrink: 0, marginTop: '2px' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: s.textColor, marginBottom: '4px' }}>
          {title}
        </div>
        {message && (
          <div style={{ fontSize: '13px', color: s.textColor, opacity: 0.85 }}>
            {message}
          </div>
        )}
        {action && (
          <div style={{ marginTop: '10px' }}>
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SignupStatusBanner({ signupComplete, pendingVerification, isApproved, stripeOnboardingComplete, stripeOnboardingUrl }) {
  const handleResumeSignup = () => {
    window.location.href = '/guides/signup';
  };

  // Banner 1: incomplete signup
  if (!signupComplete) {
    return (
      <Banner
        variant="warning"
        icon={AlertCircle}
        title="Complete your guide signup"
        message="Finish setting up your profile to get listed on OutdoorGuides."
        action={
          <button
            onClick={handleResumeSignup}
            style={{
              background: '#d97706',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Continue signup
          </button>
        }
      />
    );
  }

  // Banner 2: pending verification
  if (pendingVerification && !isApproved) {
    return (
      <Banner
        variant="info"
        icon={Clock}
        title="Your application is under review"
        message="We're reviewing your documents and credentials. This usually takes 1–2 business days."
      />
    );
  }

  // Banner 3: approved but Stripe not set up
  if (isApproved && !stripeOnboardingComplete) {
    return (
      <Banner
        variant="warning"
        icon={CreditCard}
        title="Set up payouts to start accepting bookings"
        message={<>Connect your bank account so you can receive payments from guests. <a href="/guide/dashboard/settings/payments" style={{ color: '#92400e', fontWeight: 600 }}>Go to payment settings</a>.</>}
        action={stripeOnboardingUrl && (
          <a
            href={stripeOnboardingUrl}
            style={{
              background: '#d97706',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 600,
              display: 'inline-block',
            }}
          >
            Set up payouts
          </a>
        )}
      />
    );
  }

  // Banner 4: all complete — no banner
  return null;
}
